import type { ReactElement } from 'react';
import { useCallback, useMemo } from 'react';
import { ChevronRight } from 'lucide-react';
import { DataTable } from '@/components/DataTable';
import type { DataFetchParams } from '@/components/DataTable';
import type {
  BulkToolbarContext,
  RowSelectionOptions,
} from '@/components/DataTable/types';
import type {
  AlertVisibility,
  GroupedAlert,
} from '@kirin/api-client/contracts/alerts';
import { queryKeys } from '@/hooks/api/queryKeys';
import { AlertVisibilitySelector } from '@/components/AlertVisibilitySelector';
import { useAlertVisibility } from '@/hooks/useAlertVisibility';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { useAlertsColumnCustomization } from '@/hooks/preferences/useAlertsColumnLayout';
import { AlertsBulkActionBar } from '@/components/AlertsBulkActions/AlertsBulkActionBar';
import {
  createUserAlertsColumns,
  createUserAlertsActionsColumn,
} from './columns';
import { fetchUserAlerts } from './api';
import { TableRowExpanded } from './TableRowExpanded';

const ACCOUNT_SCOPE_KEY = 'account';

/**
 * Build the query key for the user alerts page. `visibility` is appended
 * only when it is not `'active'` so toggling away from the default
 * triggers a refetch instead of returning the previously-cached
 * default-list page. The default value is omitted entirely so the cache
 * key for the default-list baseline stays byte-identical to the
 * pre-KIR-2512 wire shape.
 */
function getQueryKeysFromParams(
  params: DataFetchParams,
  visibility: AlertVisibility
) {
  const baseKey = queryKeys.alerts.listFiltered({
    page: params.pagination.pageIndex + 1,
    limit: params.pagination.pageSize,
    filters: params.filters,
    sortBy: params.sort?.field,
    sortOrder: params.sort?.order,
    search: params.search,
  });
  if (visibility === 'active') return baseKey;
  return [...baseKey, { visibility }];
}

/**
 * DataTable-based alerts tab for the user dashboard.
 */
export function DataTableUserAlertsContent(): ReactElement {
  const { flags } = useFeatureFlags([flagsmithFeatureFlags.ALERTS_REVAMP]);
  const revampEnabled = flags[flagsmithFeatureFlags.ALERTS_REVAMP] === true;

  const { visibility, setVisibility } = useAlertVisibility(ACCOUNT_SCOPE_KEY);

  // KIR-4089: per-user show/hide/reorder (flag-gated; undefined = fixed set).
  const columnCustomization = useAlertsColumnCustomization();

  // KIR-4700: Actions comes before the base columns (ahead of "Last Seen")
  // instead of trailing them. The "Add to Allow/Block List" action (KIR-3558)
  // lives in the expanded row's "DETECTED ON CLIENT" panel, not this column.
  const columns = useMemo(
    () => [
      createUserAlertsActionsColumn(),
      ...createUserAlertsColumns(revampEnabled),
    ],
    [revampEnabled]
  );

  const searchConfig = {
    placeholder: 'Search alerts',
    className: 'w-[260px]',
    inputProps: { 'aria-label': 'Search alerts' },
  };

  const queryFn = useCallback(
    (params: DataFetchParams, signal?: AbortSignal) =>
      fetchUserAlerts(
        params,
        signal,
        visibility === 'active' ? undefined : visibility
      ),
    [visibility]
  );

  const queryKeyFn = useCallback(
    (params: DataFetchParams) => getQueryKeysFromParams(params, visibility),
    [visibility]
  );

  // Visibility affects total count — include it in the knownTotal fingerprint
  // so toggling visibility invalidates the cached total.
  const countScope = useMemo(() => ({ visibility: visibility }), [visibility]);

  // Dim a row when ANY suppression state is set. See the org container for
  // the full rationale on why OR is the right combinator here AND why the
  // dim is scoped to non-Actions cells via the
  // `[&>td:not([data-column-id='actions'])]` arbitrary variant rather than
  // applied at row-level (short version: CSS opacity cascades to all
  // descendants and would dim the Mute/Unmute/Create-Exclusion/
  // Remove-Exclusion buttons, making them appear unavailable when they are
  // in fact the primary affordance to un-suppress the row). Targeting the
  // Actions cell by its stable `data-column-id` (set by `TableBody`) — not
  // `:last-child` — keeps this correct now that KIR-4700 moved Actions to
  // lead the column set instead of trailing it.
  const getRowClassName = useCallback(
    (row: GroupedAlert): string | undefined =>
      row.mute?.muted || row.exclude?.excluded
        ? "[&>td:not([data-column-id='actions'])]:opacity-60"
        : undefined,
    []
  );

  // Bulk selection (KIR-3252, personal scope). NO role gate: personal alerts
  // are the user's own data, so unlike the org tab there is no owner/manager
  // check. `scope: 'account'` routes the shared bulk bar to the
  // `/account/alerts/bulk/*` endpoints.
  const rowSelectionOptions = useMemo<RowSelectionOptions<GroupedAlert>>(
    () => ({
      getRowId: (row: GroupedAlert) => row.alertKey,
      // Tabs (Active/All/Suppressed) swap the list without touching
      // filters/search, so feed visibility in as the reset token to clear
      // the selection on tab change.
      resetToken: visibility,
      // The table fills a fixed box; the bar hangs in the space below.
      hangBulkToolbar: true,
      renderBulkToolbar: (ctx: BulkToolbarContext<GroupedAlert>) => (
        <AlertsBulkActionBar
          ctx={ctx}
          scope={{ kind: 'account' }}
          visibility={visibility}
        />
      ),
    }),
    [visibility]
  );

  // KIR-2748: keep the table inside ONE viewport-constrained flex column so
  // it never pushes the page column past 100vh (the outer-scroll bug). The
  // box does not clip: the bulk bar hangs just below it, in the room above
  // the "Contact us" footer (KIR-5379). The DataTable flexes to fill the space (`flex-1
  // min-h-0`) and keeps its own inner overflow scroll, so any table overflow
  // stays inside the table, never the page. The visibility selector rides in
  // the table toolbar rather than a row of its own, so the table starts higher.
  return (
    <div className="flex flex-col h-[calc(100vh-11rem)]">
      {/*
        ⚠️ KIR-2659 deep-link contract: the `id="user-alt"` value is
        also baked into URLs constructed by the desktop agent + IDE
        extension when they build summary-banner "View Details" deep
        links (see extensions/agent/pkg/notifications/coordinator.go
        and extensions/vscode-core/src/services/notifications/
        coordinator.ts — both ship URLs like
        `/dashboard/alerts?user-alt.sev[includes]=high&...`).
        Renaming this id breaks every notification deep-link to the
        personal alerts view. Update the agent + extension URL
        builders in the same PR if you change it.
      */}
      <DataTable<GroupedAlert>
        id="user-alt"
        mode="server"
        className="flex flex-col flex-1 min-h-0"
        containerClassName="flex-1"
        columns={columns}
        columnCustomization={columnCustomization}
        errorBoundaryLabel="Something went wrong in the Alerts table"
        search={searchConfig}
        toolbarEndSlot={
          <AlertVisibilitySelector
            scopeKey={ACCOUNT_SCOPE_KEY}
            value={visibility}
            onChange={setVisibility}
          />
        }
        features={{ enableRowExpansion: true, enableSorting: true }}
        rowExpansion={{
          icon: <ChevronRight className="h-4 w-4" />,
          render: row => <TableRowExpanded row={row} />,
          contentClassName: 'bg-muted/30 p-4',
          rowClassName: 'bg-muted/10',
          keepMounted: false,
          showContainer: false,
        }}
        queryFn={queryFn}
        getQueryKeysFromParams={queryKeyFn}
        countScope={countScope}
        rowSelection={rowSelectionOptions}
        getRowClassName={getRowClassName}
        noDataLabel="No alerts found."
        queryConfig={{
          staleTime: 30000,
          refetchOnWindowFocus: false,
        }}
      />
    </div>
  );
}

DataTableUserAlertsContent.displayName = 'DataTableUserAlertsContent';
